import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../../middleware/api';
import API from '../../../routes/APIEndpoints';
import categoriesUtils from '../../../utils/categories.utils';
import TagInput from '../../Common/Inputs/TagInput/TagInput';
import BasicInput from '../../Common/Inputs/BasicInput/BasicInput';
import Dropdown from '../../Common/Dropdown/Dropdown';
import TextareaInput from '../../Common/Inputs/TextareaInput/TextareaInput';
import { toast } from 'react-toastify';
import mixturesSchema from '../../../schema/mixtures.schema';
import { formatMixtureRef } from '../../../utils/format.utils';
import { createPortal } from 'react-dom';

const MixtureDetails = ({ isOpen, onClose, mixture = null }) => {
    const queryClient = useQueryClient();
    const [dilutions, setDilutions] = useState([]);
    const [mixturesCount, setMixturesCount] = useState(0);

console.log(mixture)

    const handlePrincipalCategoryChange = (e) => {
        formik.setFieldValue('category', e.target.value);
        formik.setFieldValue('subCategory', categoriesUtils.getCategoriesByParent(e.target.value)[0].label);
    };

    const handleRefChange = (e) => {
        const value = parseInt(e.target.value);
        if (isNaN(value)) return;
        formik.setFieldValue('ref', value);
    };

    const createMutation = useMutation({
        mutationFn: (newMixture) => {
            return api.post('/mixtures/catalogue/create', newMixture);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mixtures'] });
            onClose();
            toast.success('Mélange créé avec succès');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erreur lors de la création du mélange');
        }
    });

    const updateMutation = useMutation({
        mutationFn: (updatedMixture) => {
            return api.patch(`/mixtures/${updatedMixture.id}`, updatedMixture);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mixtures'] });
            onClose();
            toast.success('Mélange mis à jour avec succès');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erreur lors de la mise à jour du mélange');
        }
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => {
            return api.delete(`/mixtures/${id}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['mixtures'] });
            onClose();
            toast.success('Mélange supprimé avec succès');
        },
        onError: (error) => {
            toast.error(error.response?.data?.message || 'Erreur lors de la suppression du mélange');
        }
    });

    const formik = useFormik({
        initialValues: {
            name: mixture?.name || '',
            reagents: mixture?.reagents || [],
            category: mixture?.category || categoriesUtils.getParentCategories()[0],
            subCategory: mixture?.subCategory || categoriesUtils.getCategoriesByParent(categoriesUtils.getParentCategories()[0])[0].label,
            dilution: mixture?.defaultDilution?.label || '',
            ref: mixture?.ref?.split('-')[1] || '',
            description: mixture?.description || ''
        },
        validationSchema: mixturesSchema.createCatalogueMixture,
        onSubmit: async (values) => {
            const dilution = dilutions.find(d => d.label === values.dilution);
            const ref = categoriesUtils.getRefFromCategory(values.subCategory) + '-' + formatMixtureRef(values.ref);
            const newMixture = { ...values, ref, dilution, dilutionId: dilution.id };

            if (mixture) {
                await updateMutation.mutateAsync({ ...newMixture, id: mixture.id });
            } else {
                await createMutation.mutateAsync(newMixture);
            }
        }
    });

    useEffect(() => {
        if (dilutions.length > 0 && !formik.values.dilution) {
            formik.setFieldValue('dilution', dilutions[0].label);
        }
    }, [dilutions]);

    useEffect(() => {
        if (mixturesCount > 0 && !mixture && !formik.values.ref) {
            const value = mixturesCount + 1;
            formik.setFieldValue('ref', value < 10 ? `0${value}` : String(value));
        }
    }, [mixturesCount]);

    useEffect(() => {
        const fetchDilutions = async () => {
            const response = await api.get('/dilutions');
            if (response.status === 200) {
                setDilutions(response.data.values);
            }
        };
        fetchDilutions();
    }, []);

    useEffect(() => {
        if (formik.values.subCategory) {
            const fetchMixturesCount = async () => {
                const response = await api.get(`/mixtures/catalogue/count/${formik.values.subCategory}`);
                if (response.status === 200) {
                    setMixturesCount(response.data.count);
                }
            };
            fetchMixturesCount();
        }
    }, [formik.values.subCategory]);

    useEffect(() => {
        if (mixture) {
            formik.setValues({
                name: mixture.name,
                reagents: mixture.reagents,
                category: mixture.category,
                subCategory: mixture.subCategory,
                dilution: mixture.defaultDilution.label,
                ref: mixture.ref,
                description: mixture.description
            });
        }
    }, [mixture]);

    if (!isOpen) return null;

    return createPortal(
        <div className="modal modal-open">
            <div className="modal-box">
                <button 
                    className="btn btn-sm btn-circle btn-ghost absolute right-2 top-2"
                    onClick={onClose}
                >
                    ✕
                </button>
                <h3 className="font-bold text-lg mb-4">
                    {mixture ? 'Modifier le mélange' : 'Ajouter un mélange'}
                </h3>
                <form onSubmit={formik.handleSubmit}>
                    <div className="form-control w-full mb-3">
                        <label className="label">
                            <span className="label-text">Nom</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            className="input input-bordered w-full"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            required
                        />
                    </div>

                    <div className="flex gap-4 sm:flex-wrap self-center mb-3">
                        <div className="w-full">
                            <Dropdown
                                id="category"
                                name="category"
                                onChange={handlePrincipalCategoryChange}
                                value={formik.values.category}
                                options={categoriesUtils.getParentCategories()}
                                error={formik.errors?.category}
                                touched={formik.touched?.category}
                            />
                        </div>
                        <div className="w-full">
                            <Dropdown
                                id="subCategory"
                                name="subCategory"
                                onChange={formik.handleChange}
                                value={formik.values.subCategory}
                                options={categoriesUtils.getCategoriesByParent(formik.values.category).map(category => category.label)}
                                error={formik.errors?.subCategory}
                                touched={formik.touched?.subCategory}
                            />
                        </div>
                        <div className="w-2/3">
                            <Dropdown
                                id="dilution"
                                name="dilution"
                                onChange={formik.handleChange}
                                value={formik.values.dilution}
                                options={dilutions.map(d => d.label)}
                                error={formik.errors?.dilution}
                                touched={formik.touched?.dilution}
                            />
                        </div>
                    </div>

                    <div className="form-control w-full mb-3">
                        <label className="label">
                            <span className="label-text">Référence</span>
                        </label>
                        <input
                            type="text"
                            name="ref"
                            className="input input-bordered w-full"
                            value={formik.values.ref}
                            onChange={handleRefChange}
                            required
                        />
                    </div>

                    <div className="form-control w-full mb-3">
                        <label className="label">
                            <span className="label-text">Description</span>
                        </label>
                        <textarea
                            name="description"
                            className="textarea textarea-bordered w-full"
                            value={formik.values.description}
                            onChange={formik.handleChange}
                        />
                    </div>

                    <div className="form-control w-full mb-3">
                        <label className="label">
                            <span className="label-text">Réactifs</span>
                        </label>
                        <TagInput
                            formik={formik}
                            field="reagents"
                            placeholder="Ajouter un réactif"
                        />
                    </div>

                    <div className="modal-action">
                        <button
                            type="button"
                            className="btn"
                            onClick={onClose}
                            disabled={createMutation.isPending || updateMutation.isPending}
                        >
                            Annuler
                        </button>
                        {mixture && (
                            <button
                                type="button"
                                className="btn btn-error"
                                onClick={() => {
                                    if (confirm('Êtes-vous sûr de vouloir supprimer ce mélange ?')) {
                                        deleteMutation.mutate(mixture.id);
                                    }
                                }}
                                disabled={deleteMutation.isPending}
                            >
                                {deleteMutation.isPending ? 'Suppression...' : 'Supprimer'}
                            </button>
                        )}
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={createMutation.isPending || updateMutation.isPending}
                        >
                            {createMutation.isPending || updateMutation.isPending
                                ? 'Enregistrement...'
                                : mixture
                                    ? 'Mettre à jour'
                                    : 'Créer'}
                        </button>
                    </div>
                </form>
            </div>
            <div className="modal-backdrop bg-neutral-900/60" onClick={onClose}></div>
        </div>,
        document.body
    );
};

export default MixtureDetails; 